import React from 'react';
import { useLocation } from 'react-router-dom';
import './PolicyComponent.css';

interface PolicyProps {
  type: 'privacy' | 'tos';
}

const PolicyComponent: React.FC<PolicyProps> = ({ type }) => {
  const location = useLocation();

  const privacyPolicy = {
    title: "Vivid Besties – Privacy Policy",
    effectiveDate: "2025 02 19",
    sections: [
      {
        title: "1. Introduction",
        content: "This Privacy Policy explains how Vivid Besties ('we', 'our', 'us') collects, uses, and protects your information when you use our App. By using the App, you agree to the practices described herein."
      },
      {
        title: "2. Information We Collect",
        content: "• Personal Information: When you register, we collect your name, email address, profile picture, social media handles, and other contact details.\n\n• User-Generated Content: Any content you post (e.g., thoughts, application materials, comments) is stored on our servers.\n\n• Usage Data: We collect data on how you interact with the App (e.g., pages viewed, time spent, clicks).\n\n• Device and Log Data: Information from your device (IP address, device type, operating system) may be collected automatically."
      },
      {
        title: "3. How We Use Your Information",
        content: "We use your information to:\n\n• Provide and improve our services.\n• Personalize your experience and recommend collabs.\n• Communicate with you about updates, promotions, and support.\n• Analyze usage and performance to optimize the App.\n• Enforce our Terms and protect our users."
      },
      {
        title: "4. Sharing and Disclosure",
        content: "We do not sell your personal data. We may share information with:\n\n• Service Providers: Third parties who perform services on our behalf.\n• Legal Authorities: When required by law or to protect our rights.\n• Business Transfers: In connection with mergers, acquisitions, or asset sales, subject to confidentiality obligations."
      },
      {
        title: "5. Data Security",
        content: "We implement industry-standard security measures to protect your information from unauthorized access. However, no method is 100% secure, and we cannot guarantee absolute security."
      },
      {
        title: "6. International Data Transfers",
        content: "Your information may be transferred to and maintained on servers located outside your country. We ensure that such transfers comply with applicable data protection laws."
      },
      {
        title: "7. Your Rights and Choices",
        content: "You may access, update, or delete your account information by contacting us. You can opt-out of promotional emails, though you may still receive essential notifications. For data access or deletion requests, please contact: privacy@vividbesties.com."
      },
      {
        title: "8. Children's Privacy",
        content: "Our services are not directed to individuals under 18. We do not knowingly collect personal information from minors. If you believe we have inadvertently done so, please contact us immediately."
      },
      {
        title: "9. Changes to This Privacy Policy",
        content: "We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated effective date. Continued use of the App constitutes acceptance of any changes."
      },
      {
        title: "10. Contact Information",
        content: "For any questions about this Privacy Policy, please contact us at:\n\nEmail: contact@pixell.global\n\nAddress: 469 W Huron St, APT 1210, Chicago, IL, 60654"
      }
    ]
  };

  const termsOfService = {
    title: "Vivid Besties Terms of Service",
    effectiveDate: "2025 02 19",
    sections: [
      {
        title: "1. Introduction and Acceptance",
        content: "Welcome to Vivid Besties ('we', 'our', 'us'). These Terms of Service ('Terms') govern your use of the Vivid Besties app ('App'), a platform where influencers ('Besties') share their thoughts and apply for collaborations ('Collabs'). By accessing or using our App, you agree to these Terms. If you do not agree, please do not use our App."
      },
      {
        title: "2. Eligibility",
        content: "You must be at least 18 years old to use this App. By using the App, you represent and warrant that you are legally capable of entering into these Terms."
      },
      {
        title: "3. Description of Services",
        content: "Vivid Besties provides an online platform for Besties to create profiles, share content, and apply for collabs with brands. We facilitate communication and may provide tools to manage collaboration applications and related messaging."
      },
      {
        title: "4. User Accounts and Security",
        content: "You must register an account to use our services. You agree to provide accurate, current information and keep your login credentials secure. You are responsible for all activity under your account. Notify us immediately of any unauthorized use."
      },
      {
        title: "5. User Conduct",
        content: "You agree not to:\n\n• Violate any laws or regulations.\n• Post content that is unlawful, defamatory, obscene, or infringing on any intellectual property rights.\n• Engage in harassment or abusive behavior.\n• Use automated tools to access or disrupt our services."
      },
      {
        title: "6. Content and Intellectual Property",
        content: "You retain ownership of the content you create. By posting, you grant us a non-exclusive, royalty-free, worldwide license to use, display, and distribute your content for purposes of operating and promoting the App. You represent that you have rights to all content you post."
      },
      {
        title: "7. Collaboration Applications",
        content: "Besties can apply for collabs. We do not guarantee any collab or endorsement. All arrangements between Besties and brands are solely between the parties. We are not liable for disputes arising from such collaborations."
      },
      {
        title: "8. Disclaimers and Limitation of Liability",
        content: "Our App is provided 'as is' without warranties of any kind. We do not guarantee uninterrupted or error-free service. In no event shall we be liable for any indirect, incidental, or consequential damages arising from your use of the App."
      },
      {
        title: "9. Indemnification",
        content: "You agree to indemnify and hold harmless Vivid Besties and its affiliates from any claims, damages, or expenses arising from your breach of these Terms or misuse of our services."
      },
      {
        title: "10. Termination",
        content: "We may suspend or terminate your account at our discretion for any breach of these Terms. Upon termination, your right to use the App will immediately cease."
      },
      {
        title: "11. Governing Law and Dispute Resolution",
        content: "These Terms are governed by the laws of State of Delaware. Any disputes will be resolved through binding arbitration or in the appropriate courts of State of Delaware."
      },
      {
        title: "12. Changes to Terms",
        content: "We may modify these Terms at any time. Continued use of the App signifies your acceptance of the updated Terms. We will notify you of material changes via the App or email."
      },
      {
        title: "13. Contact Information",
        content: "For questions or concerns about these Terms, please contact us at:\n\nEmail: contact@pixell.global\n\nAddress: 469 W Huron St, APT 1210, Chicago, IL, 60654"
      }
    ]
  };

  const policy = type === 'privacy' ? privacyPolicy : termsOfService;

  return (
    <div className="policy-container">
      <div className="policy-content">
        <h1 className="policy-title">{policy.title}</h1>
        <p className="policy-date">Effective Date: {policy.effectiveDate}</p>
        
        {policy.sections.map((section, index) => (
          <div key={index} className="policy-section">
            <h2 className="section-title">{section.title}</h2>
            <p className="section-content">{section.content}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PolicyComponent;